import type { VerifyPushRequest, VerifyStatusErrorsResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorBody } from '@/services/api/errors';
import type { PushApiError, PushPlatform, PushProvider } from '../types';
import { objectFields, stringField } from '../utils/object-fields';

/** The title every test push is sent with. */
export const PUSH_TEST_TITLE = 'TEST';

/** The body every test push is sent with. */
export const PUSH_TEST_BODY = "If you're reading this, it means your integration with Customer.io is working.";

export type PushTestRequestInput = {
  /** The device token the test is sent to. */
  to: string;
  /**
   * Sent when the caller knows which credential to use. The push settings pages
   * name the provider; an app-scoped test names the platform instead and lets
   * the backend resolve the provider from that app's platform credential.
   */
  provider?: PushProvider;
  platform?: PushPlatform;
  /** Send through the provider's development endpoint. */
  sandbox?: boolean;
  /**
   * An unsaved credential to test with, as the JSON string the credential's
   * password field would hold. Only used before a provider is configured.
   */
  credentials?: string;
  /** Scopes the test to one app's own credentials. */
  app_id?: number;
};

/**
 * The full `verify/push` body for a test send.
 *
 * The request schema declares every field, so the ones a plain test push does
 * not use are sent as their zero value: a test carries no sound, image, link,
 * priorities or custom data, and is neither a rich push nor a raw JSON payload.
 */
export function buildPushTestRequest({
  to,
  provider,
  platform,
  sandbox = false,
  credentials = '',
  app_id: appId,
}: PushTestRequestInput): VerifyPushRequest {
  return {
    to: to.trim(),
    title: PUSH_TEST_TITLE,
    body: PUSH_TEST_BODY,
    provider: provider ?? '',
    platform: platform ?? '',
    sandbox,
    credentials,
    app_id: appId ?? null,
    android_priority: '',
    ios_priority: '',
    custom_data: '',
    image_url: '',
    link: '',
    sound: '',
    is_rich: false,
    use_json: false,
  };
}

/** Send a test push. Resolves on success; rejects with the API error otherwise. */
export function sendTestPush(workspaceId: number, body: VerifyPushRequest): Promise<VerifyStatusErrorsResponse> {
  return hydraUiApi.verify.push({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

/**
 * Rebuild one error entry from an untrusted body, or null when it is not one.
 *
 * The detail is what makes an entry renderable, so it is the only thing
 * required. The schema also declares a status, but an entry that arrives
 * without one still says something worth showing.
 */
function parsePushApiError(value: unknown): PushApiError | null {
  const fields = objectFields(value);
  const detail = stringField(fields, 'detail');
  if (detail == null) {
    return null;
  }

  const parsed: PushApiError = { detail, status: stringField(fields, 'status') ?? '' };

  const code = stringField(fields, 'code');
  if (code != null) {
    parsed.code = code;
  }

  const pointer = stringField(objectFields(fields.get('source')), 'pointer');
  if (pointer != null) {
    parsed.source = { pointer };
  }

  const meta = metaField(fields.get('meta'));
  if (meta != null) {
    parsed.meta = meta;
  }

  return parsed;
}

/**
 * An error's `meta`, kept whole. Some providers explain themselves there rather
 * than in a code — Firebase puts its own rejection message in
 * `meta.full_error_message` — so the fields are carried through untouched for
 * whatever renders the error to read.
 */
function metaField(value: unknown): NonNullable<PushApiError['meta']> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return Object.fromEntries(objectFields(value));
}

/**
 * The error entries carried by a failed request, or an empty list when the
 * rejection is not an API error response. Entries with nothing to say — no
 * `detail` — are dropped, so callers can render every entry they are handed.
 */
export function extractPushApiErrors(error: unknown): PushApiError[] {
  const body = getApiClientErrorBody(error);
  if (body == null || typeof body !== 'object' || !('errors' in body)) {
    return [];
  }
  const { errors } = body;
  if (!Array.isArray(errors)) {
    return [];
  }
  return errors.flatMap((entry: unknown) => {
    const parsed = parsePushApiError(entry);
    return parsed == null ? [] : [parsed];
  });
}
